import { useCallback, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import FileExplorer from "../../Components/FilesExplorer/FileExplorer";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest } from "../../authConfig";
import { PageLayout2 } from "../../Components/PageLayout/PageLayout2";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { Col, Container, Nav, Row, Tab } from "react-bootstrap";
import FileUpload from "../../Components/FileUpload/FileUpload";
import { ChatFilesManagementService } from "../../Services/ChatFilesManagementService";
import NewFolderModal from "../../Components/NewFolderModal/NewFolderModal";
import ConfirmationModal from "../../Components/ConfirmationModal/ConfirmationModal";
import StorageProgressBar from "../../Components/StorageProgressBar/StorageProgressBar";
import './ChatFilesPage.css';
import { ConfigurationDataService } from "../../Services/ConfigurationParameterDataService";
import InformationModal from "../../Components/InformationModal/InformationModal";
import { UsersDataService } from "../../Services/UsersDataService";
import { AzureSearchDataService } from "../../Services/AzureSearchDataService";

const ChatFilesPageContent = () => {
    const { instance, accounts, inProgress } = useMsal();
    const [files, setFiles] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [cutOrCopy, setCutOrCopy] = useState(null); // "cut" or "copy"
    const [cutOrCopyItems, setCutOrCopyItems] = useState([]);
    const [showNewFolderModal, setShowNewFolderModal] = useState(false);
    const [currentFolder, setCurrentFolder] = useState({ mf: null }); // Separate state for each tab
    const [activeTab, setActiveTab] = useState('mf'); // Manage current tab
    const [path, setPath] = useState({ mf: [{ name: 'Root', id: null }] }); // Separate path for each tab
    const [sortOrder, setSortOrder] = useState('default');
    const [view, setView] = useState('list');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [storage, setStorage] = useState({ usedStorage: 0, storagePlan: { storageLimit: 1 } });
    const [configParameters, setConfigParameters] = useState({ EnableFileUploads: false, AllowedFileExtensions: '' });
    const [uploadCount, setUploadCount] = useState(0);
    const [uploadErrors, setUploadErrors] = useState([]);
    const [showUploadErrors, setShowUploadErrors] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const fileUploadRef = useRef(null);

    const fetchFiles = async (folderId = null, tab = 'mf') => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const fetchedFiles = await ChatFilesManagementService.listFilesAndFolders(tab, folderId, response.accessToken);
            setFiles(fetchedFiles);
        } catch (error) {
            console.error('Failed to fetch files:', error);
        }
    };

    const fetchStorage = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const userStorage = await ChatFilesManagementService.getUserStorage(response.accessToken);
            setStorage(userStorage);
        } catch (error) {
            console.error('Failed to load storage:', error);
        }
    };

    const fetchParameters = async () => {
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const userRole = await UsersDataService.getUserRole(response.accessToken);
            setConfigParameters(
                {...configParameters, 
                    EnableFileUploads: userRole == "SysAdmin",
                }
            );
        } catch (error) {
            console.error('Failed to load parameters:', error);
        }
    }

    const searchFiles = async (query) => {
        if (!query) {
            fetchFiles(currentFolder[activeTab], activeTab);
            return;
        }
        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const files = await ChatFilesManagementService.searchFilesAndFolders(query, activeTab, response.accessToken);
            setFiles(files);
        } catch (error) {
            console.error('Failed to search files:', error);
        }
    };

    const handleUploadComplete = async (uploadedFiles) => {
        fetchStorage();
        await fetchFiles(currentFolder[activeTab], activeTab);
        setSelectedFiles(uploadedFiles);
        await AzureSearchDataService.triggerIndexer('dmc-wichon-indexer');
    };

    const handleUploadClick = () => {
        fileUploadRef.current.triggerFileInput();
    };

    const handleNewFolderClick = () => {
        setShowNewFolderModal(true);
    };

    const handleCreateFolder = async (folderName) => {

        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const newFolderId = await ChatFilesManagementService.createFolder(folderName, currentFolder[activeTab], activeTab, response.accessToken);
            handleFolderCreated(newFolderId);
        } catch (err) {
            console.error('Error creating folder:', err);
        }
    };

    const handleFolderCreated = async (folderId) => {
        await fetchFiles(currentFolder[activeTab], activeTab);
        setSelectedFiles([{ id: folderId, type: 'folder' }]);
    };

    const handleItemClick = (event, item) => {
        // const itemId = item.fileId || item.folderId;
        event.stopPropagation();
        if (event.ctrlKey || event.metaKey) {
            setSelectedFiles(prevSelected => {
                if (prevSelected.includes(item.id)) {
                    return prevSelected.filter(x => x.id !== item.id);
                } else {
                    return [...prevSelected, {id: item.id, type: item.type}];
                }
            });
        } else {
            setSelectedFiles([{id: item.id, type: item.type}]);
        }
    };

    const handleItemDoubleClick = async (item) => {
        if (item.type === 'file') {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            await ChatFilesManagementService.downloadFile(item.id, item.name, response.accessToken);
        } else if (item.type === 'folder' || item.type === 'sharedFolder') {
            const newPath = [...path[activeTab], { name: item.name, id: item.id }];
            setPath({ ...path, [activeTab]: newPath });
            setCurrentFolder({ ...currentFolder, [activeTab]: item.id });
            await fetchFiles(item.id, activeTab);
        }
    };

    const handleUnselectAll = () => {
        setSelectedFiles([]);
    };

    const handleNavigate = async (index) => {
        const newPath = path[activeTab].slice(0, index + 1);
        setPath({ ...path, [activeTab]: newPath });
        setCurrentFolder({ ...currentFolder, [activeTab]: newPath[newPath.length - 1].id });
        await fetchFiles(newPath[newPath.length - 1].id, activeTab);
    };

    const handleCutClick = () => {
        setCutOrCopy('cut');
        setCutOrCopyItems(selectedFiles);
    };

    const handleCopyClick = () => {
        setCutOrCopy('copy');
        setCutOrCopyItems(selectedFiles);
    };

    const handlePasteClick = async () => {
        const request = {
            ...loginRequest,
            account: accounts[0]
        };
        const response = await instance.acquireTokenSilent(request);
        const targetFolderId = currentFolder[activeTab];

        if (cutOrCopy === 'cut') {
            await ChatFilesManagementService.moveItems(response.accessToken, cutOrCopyItems.map(x => x.id), targetFolderId);
        } else if (cutOrCopy === 'copy') {
            await ChatFilesManagementService.copyItems(response.accessToken, cutOrCopyItems.map(x => x.id), targetFolderId);
        }

        setCutOrCopy(null);
        setCutOrCopyItems([]);
        fetchStorage();
        await fetchFiles(targetFolderId, activeTab);
    };

    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const handleDeleteConfirmation = async () => {
        const request = {
            ...loginRequest,
            account: accounts[0]
        };
        const response = await instance.acquireTokenSilent(request);
        await ChatFilesManagementService.deleteItems(response.accessToken, selectedFiles.map(x => x.id));

        setSelectedFiles([]);
        await fetchFiles(currentFolder[activeTab], activeTab);
        await AzureSearchDataService.triggerIndexer('dmc-wichon-indexer');
        fetchStorage();
        setShowDeleteModal(false);
    };

    const handleSort = (order) => {
        setSortOrder(order);
    };

    const sortFiles = (files, order) => {
        const sortedFolders = files.filter(f => f.type === 'folder' || f.type === 'sharedFolder');
        const sortedFiles = files.filter(f => f.type === 'file' || f.type === 'sharedFile');

        switch (order) {
            case 'modified-desc':
                sortedFolders.sort((a, b) => new Date(b.modifiedDate) - new Date(a.modifiedDate));
                sortedFiles.sort((a, b) => new Date(b.modifiedDate) - new Date(a.modifiedDate));
                break;
            case 'modified-asc':
                sortedFolders.sort((a, b) => new Date(a.modifiedDate) - new Date(b.modifiedDate));
                sortedFiles.sort((a, b) => new Date(a.modifiedDate) - new Date(b.modifiedDate));
                break;
            case 'name-asc':
                sortedFolders.sort((a, b) => a.name.localeCompare(b.name));
                sortedFiles.sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'name-desc':
                sortedFolders.sort((a, b) => b.name.localeCompare(a.name));
                sortedFiles.sort((a, b) => b.name.localeCompare(a.name));
                break;
            case 'size-asc':
                sortedFolders.sort((a, b) => a.size - b.size);
                sortedFiles.sort((a, b) => a.size - b.size);
                break;
            case 'size-desc':
                sortedFolders.sort((a, b) => b.size - a.size);
                sortedFiles.sort((a, b) => b.size - a.size);
                break;
            default:
                return files; // No sorting applied
        }

        return [...sortedFolders, ...sortedFiles];
    };

    const handleDragStart = (e, item) => {
        e.dataTransfer.setData("itemId", item.id);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e, targetFolder) => {
        e.preventDefault();
        const itemId = e.dataTransfer.getData("itemId");
        if (itemId) {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            await ChatFilesManagementService.moveItem(response.accessToken, itemId, targetFolder.id);
            await fetchFiles(currentFolder[activeTab], activeTab);
        }
    };

    const handleKeyDown = useCallback((e) => {
        if (e.ctrlKey || e.metaKey) {
            if (e.key === 'x') {
                handleCutClick();
            } else if (e.key === 'c') {
                handleCopyClick();
            } else if (e.key === 'v') {
                handlePasteClick();
            }
        }
    }, [selectedFiles, path, cutOrCopyItems, cutOrCopy]);

    const handleFilesUpload = async (newFiles) => {
        setUploadCount(newFiles.length);
        setAlertVisible(true);

        try {
            const request = {
                ...loginRequest,
                account: accounts[0]
            };
            const response = await instance.acquireTokenSilent(request);
            const existingFileNames = files.filter(f => f.type === 'file').map(file => file.name);

            const uploadedFileIds = [];
            const uploadedFileErrors = [];

            const uploadPromises = newFiles.map(async file => {
                let fileName = file.name;
                let counter = 1;

                while (existingFileNames.includes(fileName)) {
                    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
                    const ext = file.name.split('.').pop();
                    fileName = `${nameWithoutExt}(${counter}).${ext}`;
                    counter++;
                }

                const formData = new FormData();
                formData.append('file', file, fileName);

                const result = await ChatFilesManagementService.uploadFile(formData, currentFolder[activeTab], activeTab, response.accessToken);
                if(result && result.status == 200){
                    uploadedFileIds.push({ id: result.data, type: 'file' });
                }
                else{
                    uploadedFileErrors.push(`Failed to upload ${file.name}: ${result?.data || "Unknown error"}`);
                }
            });

            await Promise.all(uploadPromises);

            setAlertVisible(false);
            handleUploadComplete(uploadedFileIds);

            if (uploadedFileErrors.length > 0) {
                setUploadErrors(uploadedFileErrors); // Update the uploadErrors state
                setShowUploadErrors(true); // Show the InformationModal if there are errors
            }

        } catch (error) {
            console.error('Error uploading files:', error);
            setAlertVisible(false);
        } finally {
            // Reset the input value to allow re-selecting the same file
            if (fileUploadRef.current) {
                fileUploadRef.current.value = ''; 
            }
        }
    };

    const handleTabSelect = (selectedTab) => {
        setActiveTab(selectedTab);
        setPath(prevPath => ({
            ...prevPath,
            [selectedTab]: [{ name: selectedTab === 'mf' ? 'Root' : selectedTab === 'swm' ? 'Root' : 'Root', id: null }]
        }));
        setCurrentFolder({ ...currentFolder, [selectedTab]: null });
    };

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchFiles(currentFolder[activeTab], activeTab);
        }
    }, [inProgress, accounts, instance, currentFolder, activeTab]);

    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchStorage();
            fetchParameters();
        }
    }, [inProgress, accounts, instance]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    return (
        <Container fluid className='d-flex flex-column flex-grow-1 h-100 NewTaskPage p-4 justify-content-between'>
            <Row>
                {/* <Col className='text-left'>
                    <h5 className='mb-0'>My Files</h5>
                </Col> */}
            </Row>
            <Nav variant="pills" className="simple-pills" defaultActiveKey='mf' onSelect={handleTabSelect}>
                <Nav.Item>
                    <Nav.Link eventKey="mf">Wichon's Files</Nav.Link>
                </Nav.Item>
            </Nav>
            <FileExplorer 
                files={sortFiles(files, sortOrder)}
                onAddFileClick={handleUploadClick} 
                selectedItems={selectedFiles} 
                onAddFolderClick={handleNewFolderClick} 
                onItemClick={handleItemClick}
                onItemDoubleClick={handleItemDoubleClick}
                onContainerClick={handleUnselectAll}
                path={path[activeTab]}
                currentItem={currentFolder[activeTab]}
                cutCopyEnabled={selectedFiles.length > 0}
                pasteEnabled={cutOrCopyItems.length > 0}
                onNavigate={handleNavigate}
                onSearch={searchFiles}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onCopyClick={handleCopyClick}
                onCutClick={handleCutClick}
                onPasteClick={handlePasteClick}
                onSort={handleSort}
                onDelete={handleDeleteClick}
                view={view}
                switchView={setView}
                onFilesDropped={handleFilesUpload}
                activeTab={activeTab}
                configParameters={configParameters}
            />
            {storage && <StorageProgressBar storage={storage} />}
            <FileUpload 
                ref={fileUploadRef} 
                onUploadComplete={handleUploadComplete} 
                currentFolder={currentFolder[activeTab]} 
                uploadCount={uploadCount}
                alertVisible={alertVisible}
                setUploadCount={setUploadCount}
                setAlertVisible={setAlertVisible}
                handleFilesUpload={handleFilesUpload}
                allowedExtensions={configParameters.AllowedFileExtensions}
            />
            <NewFolderModal
                show={showNewFolderModal}
                handleClose={() => setShowNewFolderModal(false)}
                handleCreateFolder={handleCreateFolder}
                existingFolders={files.filter(f => f.type === 'folder').map(f => f.name)}
            />
            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                title="Delete confirmation"
                content={`You are about to delete ${selectedFiles.length} items permanently. This action can not be reversed. Do you want to continue?`}
                onAcceptClick={handleDeleteConfirmation}
                onCancelClick={() => setShowDeleteModal(false)}
            />
            <InformationModal
                show={showUploadErrors}
                onHide={() => setShowUploadErrors(false)}
                title="Upload Errors"
                content={
                    <ul>
                        {uploadErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                        ))}
                    </ul>
                }
                onAcceptClick={() => setShowUploadErrors(false)}
            />
        </Container>
    );
}

const ChatFilesPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Wichon's Files Explorer">
                <ChatFilesPageContent />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
}

export default ChatFilesPage;
